import React, { useState, useRef, useEffect } from 'react';
import { useRoom } from '../context/RoomContext';
import { useAuth } from '../context/AuthContext';
import './MeetingMenu.css';

import { Mic, Camera, MicOff, User, X, PhoneMissed, Settings } from 'lucide-react';

const MeetingMenu = ({ onOpenSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMuteSubmenu, setShowMuteSubmenu] = useState(false);
  const [showKickSubmenu, setShowKickSubmenu] = useState(false);
  const menuRef = useRef(null);
  
  const {
    isScreenSharing,
    stopScreenShare,
    participants,
    socketId,
    room,
    muteParticipant,
    muteAllParticipants,
    kickParticipant,
    endMeeting,
  } = useRoom();
  
  const { user } = useAuth();
  
  // Check if current user is the host
  const isHost = user && room && (room.hostId === socketId);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowMuteSubmenu(false);
        setShowKickSubmenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStopScreenShare = () => {
    stopScreenShare();
    setIsOpen(false);
  };

  const handleMuteParticipant = (participantId) => {
    muteParticipant(participantId);
    setShowMuteSubmenu(false);
    setIsOpen(false);
  };

  const handleMuteAll = () => {
    muteAllParticipants();
    setIsOpen(false);
  };

  const handleKickParticipant = (participantId) => {
    kickParticipant(participantId);
    setShowKickSubmenu(false);
    setIsOpen(false);
  };

  const handleEndMeeting = () => {
    if (window.confirm('Êtes-vous sûr de vouloir terminer la réunion pour tous les participants ?')) {
      endMeeting();
    }
    setIsOpen(false);
  };

  const handleOpenSettings = () => {
    setIsOpen(false);
    if (onOpenSettings) {
      onOpenSettings();
    }
  };

  // Get other participants (excluding current user)
  const otherParticipants = participants.filter(p => p.id !== socketId);

  return (
    <div className="meeting-menu-container" ref={menuRef}>
      <button
        className="meeting-menu-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Plus d'options"
      >
        ⋮
      </button>

      {isOpen && (
        <div className="meeting-menu-dropdown">
          {/* Stop screen sharing - visible when sharing */}
          {isScreenSharing && (
            <button
              className="meeting-menu-item"
              onClick={handleStopScreenShare}
            >
              <span className="menu-icon">🖥️</span>
              <span>Arrêter le partage d'écran</span>
            </button>
          )}

          {/* Host-only features */}
          {isHost && (
            <>
              {/* Mute participant submenu */}
              <div className="menu-item-with-submenu">
                <button
                  className="meeting-menu-item"
                  onClick={() => setShowMuteSubmenu(!showMuteSubmenu)}
                >
                  <span className="menu-icon"><Mic /></span>
                  <span>Couper le micro</span>
                  <span className="submenu-arrow"><Camera /></span>
                </button>
                
                {showMuteSubmenu && (
                  <div className="submenu">
                    <button
                      className="meeting-menu-item submenu-item"
                      onClick={handleMuteAll}
                    >
                      <span className="menu-icon"> <MicOff /></span>
                      <span>Couper le micro de tout le monde</span>
                    </button>
                    
                    {otherParticipants.length > 0 && (
                      <div className="submenu-divider" />
                    )}
                    
                    {otherParticipants.map(participant => (
                      <button
                        key={participant.id}
                        className="meeting-menu-item submenu-item"
                        onClick={() => handleMuteParticipant(participant.id)}
                      >
                        <span className="menu-icon">{participant.isMuted ? <MicOff /> : <Mic />}</span>
                        <span>{participant.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Exclude participant submenu */}
              {otherParticipants.length > 0 && (
                <div className="menu-item-with-submenu">
                  <button
                    className="meeting-menu-item"
                    onClick={() => setShowKickSubmenu(!showKickSubmenu)}
                  >
                    <span className="menu-icon"><User /></span>
                    <span>Exclure un participant</span>
                    <span className="submenu-arrow"><Camera /></span>
                  </button>
                  
                  {showKickSubmenu && (
                    <div className="submenu">
                      {otherParticipants.map(participant => (
                        <button
                          key={participant.id}
                          className="meeting-menu-item submenu-item"
                          onClick={() => handleKickParticipant(participant.id)}
                        >
                          <span className="menu-icon"><X /></span>
                          <span>{participant.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* End meeting */}
              <button
                className="meeting-menu-item danger"
                onClick={handleEndMeeting}
              >
                <span className="menu-icon"><PhoneMissed /></span>
                <span>Terminer la réunion</span>
              </button>
            </>
          )}

          {/* Settings - available to all */}
          <button
            className="meeting-menu-item"
            onClick={handleOpenSettings}
          >
            <span className="menu-icon"><Settings /></span>
            <span>Paramètres</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default MeetingMenu;
